import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Flex,
  Grid,
  Radio,
  Select,
  Text,
  TextInput,
  useMantineColorScheme,
} from "@mantine/core";
import { IconCaretDownFilled } from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_GlobalFilterTextInput,
  useMantineReactTable,
} from "mantine-react-table";
import "react-datepicker/dist/react-datepicker.css";
import { DateTimePicker } from '@mantine/dates';
import DailyTimeRangeSelector from './DailyTimeRangeSelector.jsx';
import WeeklyStackedSingleRangeSelector from './WeeklyStackedSingleRangeSelector.jsx';
import { formatDate, generateDateRanges, generateWeeklySingleRangeOccurrences, toLocalDisplayDateFromUtc, toUtcWithSameClock } from "./outageLoad.constants";
import "./outageLoadManagement.scss";
import PropTypes from "prop-types";
import startDateAtom from "../../../../utils/atoms/startDateAtom";
import { useAtom } from "jotai";
import { formatInputValue, responsiveCol } from "../../../../constants/helper.js";
import {
  END_OF_DAY_HOUR_UTC,
  END_OF_DAY_MINUTE_UTC,
  END_OF_DAY_SECOND_UTC,
  END_OF_DAY_MILLISECOND_UTC,
} from "../../../../utils/helpers";
import { helpAtom } from "../../../../utils/atoms/helpAtom.js";
import { useHelpFocusBlur } from "../../../../utils/useHelpFocusBlur.js";

const OutageLoadManagement = forwardRef(({ form, handleInputChange, gridParameters, getFieldAttributes }, ref) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [outageType, setOutageType] = useState("");
  const { colorScheme } = useMantineColorScheme();
  const isLightMode = colorScheme === "light";
  const [startDateAtomData] = useAtom(startDateAtom);
  const [outageOccurDurationType, setOutageOccurDurationType] =
    useState("fullBackUp");
  const startDate =
    form?.values?.componentAttributes?.gridParameters?.outageStartDate;
  let startDateError =
    form.errors["componentAttributes.gridParameters.outageStartDate"];

  const endDate =
    form?.values?.componentAttributes?.gridParameters?.outageEndDate;
  let endDateError =
    form.errors["componentAttributes.gridParameters.outageEndDate"]; 

  // Track daily selector minutes (start/end) when outageType==='eachDay'
  const [dailyMinutes, setDailyMinutes] = useState({ start: null, end: null });
  // Track weekly stacked single range locally (do not rely on gridParameters)
  const [weeklyRange, setWeeklyRange] = useState(null);
  const [dateRangeError, setDateRangeError] = useState("");
  const [rowSelection, setRowSelection] = useState({});
  
  const [helperAtom, setHelperAtom] = useAtom(helpAtom);
  const containerRef = useRef(null);

  useHelpFocusBlur(containerRef, setHelperAtom, helperAtom);

  useImperativeHandle(ref, () => ({ getDateRangeError: () => dateRangeError }), [dateRangeError]);

  const handleAddTableRow = () => {
    if (outageType === 'eachDay') {
      if (!dailyMinutes.start && dailyMinutes.start !== 0) return; // require selection
      const baseStart = new Date(startDateAtomData);
      const baseEnd = new Date(baseStart);
      baseEnd.setUTCFullYear(baseEnd.getUTCFullYear() + 1);
      let endMinutesVal = dailyMinutes.end;
      if (endMinutesVal === 0) endMinutesVal = 1440; // interpret 0 end as full day
      const newRows = generateDateRanges('eachDay', baseStart, baseEnd, dailyMinutes.start, endMinutesVal);
      setRows(newRows);
      handleInputChange('componentAttributes', 'outageDates', newRows);
      return;
    }
    if (outageType === 'eachWeek') {
      // Use locally tracked weeklyRange from selector
      if (!weeklyRange) return; // nothing selected
      const baseStart = new Date(startDateAtomData);
      const newRows = generateWeeklySingleRangeOccurrences(baseStart, weeklyRange, 1);
      setRows(newRows);
      handleInputChange('componentAttributes', 'outageDates', newRows);
      return;
    }
    if (outageType && startDate && endDate) {
      const newRow = {
        startingTime: formatDate(startDate),
        endingTime: formatDate(endDate),
      };

      if (outageType === "noRepetition") {
        if (new Date(startDate) >= new Date(endDate)) {
          setDateRangeError(t("outage.errors.startBeforeEnd") || "Start date and time must be before end date and time");
          return;
        }
        setDateRangeError("");
        const isDuplicate = rows?.some((row) => {
          const rowFrom = new Date(row.startingTime).getTime();
          const rowTo = new Date(row.endingTime).getTime();
          const newRowFrom = new Date(newRow.startingTime).getTime();
          const newRowTo = new Date(newRow.endingTime).getTime();
          return rowFrom === newRowFrom && rowTo === newRowTo;
        });

        if (!isDuplicate) {
          const updatedRows = [...rows, newRow];
          setRows(updatedRows);
          handleInputChange("componentAttributes", "outageDates", updatedRows);
        }
        handleInputChange("componentAttributes", "outageEndDate", toUtcWithSameClock(pickerMaxEndDate));
      } else {
        const newRows = generateDateRanges(outageType, startDateAtomData, endDate);
        setRows(newRows);
        handleInputChange("componentAttributes", "outageDates", newRows);
      }
    }
  };
  const defaultStartDate = new Date(startDateAtomData);
  const defaultEndDate = (() => {
    const d = new Date(defaultStartDate);
    d.setUTCFullYear(d.getUTCFullYear() + 1);
    d.setUTCDate(d.getUTCDate() - 1);
    d.setUTCHours(
      END_OF_DAY_HOUR_UTC,
      END_OF_DAY_MINUTE_UTC,
      END_OF_DAY_SECOND_UTC,
      END_OF_DAY_MILLISECOND_UTC
    );
    return d;
  })();
  const pickerStartDate = toLocalDisplayDateFromUtc(startDate);
  const pickerEndDate = toLocalDisplayDateFromUtc(endDate);
  const pickerDefaultStartDate = toLocalDisplayDateFromUtc(defaultStartDate);
  const pickerDefaultEndDate = toLocalDisplayDateFromUtc(defaultEndDate);

  const pickerMaxEndDate = (() => { const d = new Date(pickerDefaultStartDate); d.setFullYear(d.getFullYear() + 1); d.setDate(d.getDate() - 1); d.setHours(23, 59, 0, 0); return d; })();

  const columns = useMemo(
    () => [
      { accessorKey: "startingTime", header: t("outage.table.startDateHeader"), size: "50%" },
      { accessorKey: "endingTime", header: t("outage.table.endDateHeader"), size: "50%" },
    ],
    [t]
  );

  const enableCheckbox = useMemo(
    () => outageType === "noRepetition" || outageType === "",
    [outageType]
  );

  const tableData = useMantineReactTable({
    data: rows,
    columns,
    enableColumnFilters: true,
    initialState: {
      density: "xs",
      showGlobalFilter: true,
    },
    // Explicitly control visibility of the built-in row selection column
    state: {
      columnVisibility: {
        'mrt-row-select': enableCheckbox,
      },
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    positionGlobalFilter: "right",
    mantineSearchTextInputProps: {
      placeholder: t("outage.table.searchPlaceholder"),
      sx: { minWidth: "400px" },
    },
    enableRowSelection: enableCheckbox,
    mantineTopToolbarProps: {
      sx: {
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantineBottomToolbarProps: {
      sx: {
        padding: "10px",
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantinePaperProps: {
      withBorder: false,
      shadow: false,
      sx: {
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantineTableBodyProps: {
      sx: {
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantineTableBodyCellProps: {
      sx: {
        fontWeight: "normal",
        fontSize: "14px",
        fontFamily: "Nunito",
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantineTableHeadCellProps: {
      sx: {
        fontWeight: "normal",
        fontSize: "14px",
        fontFamily: "Nunito",
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    renderTopToolbar: ({ table }) => {
      const handleDeleteRow = () => {
        const remainingRows = rows.filter(
          (row) =>
            !table
              .getSelectedRowModel()
              .flatRows.map((row) => row.original)
              .includes(row)
        );
        setRows(remainingRows);
        setRowSelection({});
        handleInputChange("componentAttributes", "outageDates", remainingRows);
      };

      const handleDeleteAll = () => {
        setRows([]);
        setRowSelection({});
        setDailyMinutes({ start: null, end: null });
        setWeeklyRange(null);
        handleInputChange("componentAttributes", "outageDates", []);
      };

      return (
        <Flex p="md" justify="space-between">
          <Flex gap="xs">
            <MRT_GlobalFilterTextInput table={table} />
          </Flex>
          {outageType === "noRepetition" && (
            <Flex sx={{ gap: "8px" }}>
              <Button
                aria-label="outage-delete-selected"
                color="gray"
                disabled={table?.getSelectedRowModel()?.flatRows?.length === 0}
                onClick={handleDeleteRow}
                variant="filled"
              >
                {t("outage.button.deleteSelected")}
              </Button>
              <Button color="gray" aria-label="outage-delete-all" onClick={handleDeleteAll} variant="filled">
                {t("outage.button.deleteAll")}
              </Button>
            </Flex>
          )}
        </Flex>
      );
    },
  });

  const gridParametersHandledChange = (value) => {
    if (rows?.length) {
      setRows([]);
      handleInputChange("componentAttributes", "outageDates", []);
    } else {
      setRows([]);
    }
    if (value === 'noRepetition') {
      handleInputChange("componentAttributes", "outageStartDate", defaultStartDate);
      handleInputChange("componentAttributes", "outageEndDate", defaultEndDate);
    }
    setDailyMinutes({ start: null, end: null });
    setWeeklyRange(null);
    if (outageType === 'noRepetition' && value !== 'noRepetition') {
      handleInputChange("componentAttributes", "outageStartDate", null);
      handleInputChange("componentAttributes", "outageEndDate", null);
    }
    setOutageType(value);
    handleInputChange("componentAttributes", "outageEvent", value);
  };

  const filterEndTime = useMemo(() => {
    return (time) => {
      if (!startDate) return true;
      const selectedDate = new Date(time);
      const isSameDate =
        selectedDate.getUTCDate() === startDate.getUTCDate() &&
        selectedDate.getUTCMonth() === startDate.getUTCMonth() &&
        selectedDate.getUTCFullYear() === startDate.getUTCFullYear();
      if (isSameDate) {
        const startTimePlusOneMinute = new Date(startDate.getTime() + 60000);
        return selectedDate >= startTimePlusOneMinute;
      }
      return true;
    };
  }, [startDate]);

  useEffect(() => {
    const rawDates = gridParameters.componentAttributes?.gridParameters?.outageDates;
    const updatedOutageDates = rawDates?.map((date) => ({
      startingTime: formatDate(new Date(date?.startingTime)),
      endingTime: formatDate(new Date(date?.endingTime)),
    }));

    if (updatedOutageDates?.length) {
      setRows(updatedOutageDates);
      // Reconstruct weeklyRange if outageEvent is eachWeek and we have at least one row
      const evt = form?.values?.componentAttributes?.gridParameters?.outageEvent;
      if (evt === 'eachWeek') {
        const first = rawDates[0];
        if (first) {
          const startDateObj = new Date(first.startingTime);
          const endDateObj = new Date(first.endingTime);
          // Derive start day/minute
          const startDay = startDateObj.getUTCDay();
          const startMinute = startDateObj.getUTCHours() * 60 + startDateObj.getUTCMinutes();
          let endDay = endDateObj.getUTCDay();
          let endMinute = endDateObj.getUTCHours() * 60 + endDateObj.getUTCMinutes();
          // Detect wrap: if end occurs before start in week index space OR spans more than a week boundary
          const wrap = (() => {
            const absStart = startDay * 1440 + startMinute;
            const absEnd = endDay * 1440 + endMinute;
            if (absEnd < absStart) return true;
            // If span crosses more than 6 days treat as wrap safety
            const daySpan = (endDateObj - startDateObj) / (1000 * 60 * 60 * 24);
            return daySpan >= 6.99 && endDay <= startDay; // rough heuristic for selections ending next week start
          })();
          // If end is exactly midnight next day we convert to 1440 boundary
          if (endDateObj.getUTCHours() === 0 && endDateObj.getUTCMinutes() === 0) {
            // Check if this midnight represents endMinute 1440 of previous day
            const prev = new Date(endDateObj.getTime() - 1 * 60 * 1000);
            if (prev.getUTCDay() === endDay) {
              // Actually stayed same day -> treat normally
            } else {
              // We advanced a day due to 24:00 boundary representation; roll back one day to compute boundary
              const adjustDay = (endDay - 1 + 7) % 7;
              endMinute = 1440; // boundary end
              // maintain endDay as original weekly selection terminal day before midnight rollover
              // For boundary we interpret endDay as day where 24:00 occurred
              endDay = adjustDay;
            }
          }
          setWeeklyRange({ startDay, startMinute, endDay, endMinute, wrap });
        }
      }
      if (evt === 'eachDay') {
        const firstDay = rawDates[0];
        if (firstDay) {
          const startObj = new Date(firstDay.startingTime);
          const endObj = new Date(firstDay.endingTime);
          const startMins = startObj.getUTCHours() * 60 + startObj.getUTCMinutes();
          let endMins = endObj.getUTCHours() * 60 + endObj.getUTCMinutes();
          if (endObj.getUTCDate() !== startObj.getUTCDate() && endObj.getUTCHours() === 0 && endObj.getUTCMinutes() === 0) {
            endMins = 1440; // full day boundary 24:00
          }
            setDailyMinutes({ start: startMins, end: endMins });
        }
      }
    }

    if (form?.values?.componentAttributes?.gridParameters?.outageEvent) {
      setOutageType(
        form?.values?.componentAttributes?.gridParameters?.outageEvent
      );
    }
  }, []);

  // If start date not set, default it to the previous calendar year's start
  // useEffect(() => {
  //   if (!startDate) {
  //     // Initialize form value for outageStartDate to defaultStartDate
  //     handleInputChange("componentAttributes", "outageStartDate", defaultStartDate);
  //   }
  // }, []); // run once on mount

  // Convert Date -> minutes from midnight helper (15-min aligned externally)
  const dateToMinutes = (d) => {
    if (!d) return null;
    return d.getUTCHours() * 60 + d.getUTCMinutes();
  };

  // When outageType is eachDay we show DailyTimeRangeSelector for time-of-day selection
  // We keep the calendar date from startDate/endDate if present; otherwise use today.
  const handleDailyRangeCommit = (startMinutes, endMinutes) => {
    if (outageType !== 'eachDay') return;
    setDailyMinutes({ start: startMinutes, end: endMinutes });
  };

  const handleOutageOccurDurationTypeChange = (value) => {
    setOutageOccurDurationType(value);
    handleInputChange("uiAttributes", "outageOccurDuration.backup", value);
  };

  useEffect(() => {
    const outageOccurType =
      form?.values?.uiAttributes?.gridParameters?.outageOccurDuration?.backup;
    if (outageOccurType === "fullBackUp") {
      handleOutageOccurDurationTypeChange(outageOccurType);
      handleInputChange("uiAttributes", "outageOccurDuration.loadValue", 100);
    } else {
      handleOutageOccurDurationTypeChange(outageOccurType || "fullBackUp");
      handleInputChange(
        "uiAttributes",
        "outageOccurDuration.loadValue",
        form?.values?.uiAttributes?.gridParameters?.outageOccurDuration
          ?.loadValue || 100
      );
    }
  }, [outageOccurDurationType]);

  return (
    <div ref={containerRef}>
      <Text
        component="span"
        color={colorScheme === "dark" ? "#d5d7e0" : "black"}
        fw={700}
      >
        {t("outageLoadManagement")}
      </Text>
      {/* Vertical stacked layout for eachDay / eachWeek */}
      {outageType === "eachDay" ||
      outageType === "eachWeek" ||
      outageType === "noRepetition" ? (
        <div
          className={`outage-inline-row ${
            outageType === "noRepetition" ? "no-repetition-inline" : ""
          }`}
        >
          <Grid style={{ flex: 1 }} gutter={"sm"} align="flex-end">
            <Grid.Col {...responsiveCol}>
              <Select
                {...getFieldAttributes("field-12")}
                data-id="field-12"
                rightSection={<IconCaretDownFilled size={18} />}
                rightSectionProps={{ style: { pointerEvents: "none" } }}
                label={t("outage.select.label")}
                data={[
                  {
                    value: "noRepetition",
                    label: t("outage.select.noRepetition"),
                  },
                  { value: "eachWeek", label: t("outage.select.eachWeek") },
                  { value: "eachDay", label: t("outage.select.eachDay") },
                ]}
                withAsterisk
                placeholder={t("outage.select.placeholder")}
                value={
                  form?.values?.componentAttributes?.gridParameters
                    ?.outageEvent || ""
                }
                {...form.getInputProps(
                  "componentAttributes.gridParameters.outageEvent"
                )}
                onChange={gridParametersHandledChange}
                withinPortal
              />
            </Grid.Col>
            {outageType === "noRepetition" && (
              <>
                <Grid.Col className="no-rep-start" {...responsiveCol}>
                  <DateTimePicker
                    {...getFieldAttributes("field-13")}
                    data-id="field-13"
                label={t("outage.startDate.label")}
                    value={pickerStartDate || null}
                    onChange={(value) => {
                      setDateRangeError("");
                      if (!value) {
                        handleInputChange(
                          "componentAttributes",
                          "outageStartDate",
                          null
                        );
                        return;
                      }

                      const clampedValue =
                        pickerDefaultStartDate && value < pickerDefaultStartDate
                          ? pickerDefaultStartDate
                          : value;

                      handleInputChange(
                        "componentAttributes",
                        "outageStartDate",
                        toUtcWithSameClock(clampedValue)
                      );
                    }}
                    withSeconds={false}
                    clearable
                    valueFormat="MMMM D, YYYY HH:mm"
                    minDate={pickerDefaultStartDate}
                    maxDate={
                      pickerEndDate || pickerDefaultEndDate
                    }
                  />
                </Grid.Col>
                <Grid.Col className="no-rep-end" {...responsiveCol} style={{ position: 'relative' }}>
                  <DateTimePicker
                    {...getFieldAttributes("field-14")}
                    data-id="field-14"
                    label={t("outage.endDate.label")}
                    value={pickerEndDate || null}
                    onChange={(value) => {
                      setDateRangeError("");
                      handleInputChange(
                        "componentAttributes",
                        "outageEndDate",
                        toUtcWithSameClock(value)
                      );
                    }}
                    withSeconds={false}
                    clearable
                    valueFormat="MMMM D, YYYY HH:mm"
                    // Bound end date to the previous calendar year and ensure it
                    // is not before the selected start date (or previous year start)
                    minDate={pickerStartDate || pickerDefaultStartDate}
                    maxDate={pickerMaxEndDate}
                    disabled={!startDate}
                  />
                  {dateRangeError && (
                    <Text size="xs" color="red" style={{ position: 'absolute', bottom: -18, left: '2%' }}>
                      {dateRangeError}
                    </Text>
                  )}
                </Grid.Col>
                <Grid.Col span={2} className="no-rep-add-btn">
                  <div className="add-btn-wrapper" style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    <Button
                      aria-label="outage-add-no-repetition"
                      color="gray"
                      variant="filled"
                      onClick={handleAddTableRow}
                      disabled={
                        (outageType === "eachDay" &&
                          (dailyMinutes.start == null ||
                            dailyMinutes.end == null)) ||
                        (outageType === "eachWeek" && !weeklyRange) ||
                        (outageType === "noRepetition" &&
                          (!startDate || !endDate))
                      }
                    >
                      {t("outage.button.add")}
                    </Button>
                    <Button
                      aria-label="outage-clear-no-repetition"
                      color="gray"
                      variant="outline"
                      onClick={() => {
                        if (outageType === 'noRepetition') {
                          handleInputChange("componentAttributes", "outageStartDate", null);
                          handleInputChange("componentAttributes", "outageEndDate", null);
                          return;
                        }
                        setRows([]);
                        setDailyMinutes({ start: null, end: null });
                        setWeeklyRange(null);
                        handleInputChange("componentAttributes", "outageDates", []);
                      }}
                    >
                      {t("outage.button.clear")}
                    </Button>
                  </div>
                </Grid.Col>
              </>
            )}
          </Grid>
          {outageType === "eachDay" && (
            <div className="daily-range-wrapper">
              <h6>{t("outage.daily.heading")}</h6>
              <DailyTimeRangeSelector
                initialStartMinutes={dailyMinutes.start}
                initialEndMinutes={dailyMinutes.end}
                onRangeCommit={handleDailyRangeCommit}
              />
              <div className="outage-hint-text">
                {t("outage.daily.hint")}
              </div>
            </div>
          )}
          {outageType === "eachWeek" && (
            <div className="weekly-range-wrapper">
              <h6>{t("outage.weekly.heading")}</h6>
              <WeeklyStackedSingleRangeSelector
                initialRange={weeklyRange}
                onRangeChange={(range) => {
                  setWeeklyRange(range);
                }}
              />
              <div className="outage-hint-text">
                {t("outage.weekly.hint")}
              </div>
            </div>
          )}

          {outageType !== "noRepetition" && (
            <div className="add-btn-wrapper" style={{ paddingBottom: 4, display:'flex', gap:8 }}>
              <Button
                aria-label="outage-add-recurring"
                color="gray"
                variant="filled"
                onClick={handleAddTableRow}
                disabled={
                  (outageType === "eachDay" &&
                    (dailyMinutes.start == null || dailyMinutes.end == null)) ||
                  (outageType === "eachWeek" && !weeklyRange) ||
                  (outageType === "noRepetition" && (!startDate || !endDate))
                }
              >
                {t("outage.button.add")}
              </Button>
              <Button
                aria-label="outage-clear-recurring"
                color="gray"
                variant="outline"
                onClick={() => {
                  setRows([]);
                  setDailyMinutes({ start: null, end: null });
                  setWeeklyRange(null);
                  if (outageType === 'noRepetition') {
                    handleInputChange("componentAttributes", "outageStartDate", null);
                    handleInputChange("componentAttributes", "outageEndDate", null);
                  }
                  handleInputChange("componentAttributes", "outageDates", []);
                }}
              >
                {t("outage.button.clear")}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <Grid gutter="md" className="mt-2 mb-2">
          <Grid.Col {...responsiveCol}>
            <Select
              {...getFieldAttributes("field-12")}
              data-id="field-12"
              rightSection={<IconCaretDownFilled size={18} />}
              rightSectionProps={{ style: { pointerEvents: "none" } }}
              label={t("outage.select.label")}
              data={[
                { value: "noRepetition", label: t("outage.select.noRepetition") },
                { value: "eachWeek", label: t("outage.select.eachWeek") },
                { value: "eachDay", label: t("outage.select.eachDay") },
              ]}
              withAsterisk
              placeholder={t("outage.select.placeholder")}
              value={
                form?.values?.componentAttributes?.gridParameters
                  ?.outageEvent || ""
              }
              {...form.getInputProps(
                "componentAttributes.gridParameters.outageEvent"
              )}
              onChange={gridParametersHandledChange}
              withinPortal
            />
          </Grid.Col>
          {/* {outageType !== "eachDay" && (
            <>
              <Grid.Col
                span={3}
                style={{ position: "relative" }}
                className={
                  colorScheme === "dark"
                    ? "outage-date-picker"
                    : "outage-date-picker-light"
                }
              >
                <Text>{"Start Date"}</Text>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => {
                    handleInputChange(
                      "componentAttributes",
                      "outageStartDate",
                      date
                    );
                  }}
                  showTimeSelect
                  timeIntervals={1}
                  dateFormat="MMMM d, yyyy HH:mm"
                  timeFormat="HH:mm"
                  name="outageStartDate"
                  {...form.getInputProps(
                    "componentAttributes.gridParameters.outageStartDate"
                  )}
                  className={`${startDateError ? "date-picker-error" : ""}`}
                  calendarClassName={isLightMode ? "" : "dark-datepicker"}
                />
                {startDateError && (
                  <Text color="red" size="sm">
                    {startDateError}
                  </Text>
                )}
              </Grid.Col>
              <Grid.Col
                span={3}
                style={{ position: "relative" }}
                className={
                  colorScheme === "dark"
                    ? "outage-date-picker"
                    : "outage-date-picker-light"
                }
              >
                <Text>{"End Date"}</Text>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => {
                    handleInputChange(
                      "componentAttributes",
                      "outageEndDate",
                      date
                    );
                  }}
                  showTimeSelect
                  timeIntervals={1}
                  dateFormat="MMMM d, yyyy HH:mm"
                  timeFormat="HH:mm"
                  minDate={startDate}
                  disabled={!startDate}
                  filterTime={filterEndTime}
                  name="outageEndDate"
                  {...form.getInputProps(
                    "componentAttributes.gridParameters.outageEndDate"
                  )}
                  className={`${endDateError ? "date-picker-error" : ""}`}
                  calendarClassName={isLightMode ? "" : "dark-datepicker"}
                />
                {endDateError && (
                  <Text color="red" size="sm">
                    {endDateError}
                  </Text>
                )}
              </Grid.Col>
            </>
          )} */}
          {/* Weekly previously used grid; now inline layout handles rendering */}
          <Grid.Col
            className={`${
              startDateError || endDateError
                ? "outage-error"
                : "outage-add-button"
            }`}
            {...responsiveCol}
          >
            {outageType && (
              <div style={{ display:'flex', gap:8 }}>
                <Button aria-label="outage-add-generic" color="gray" variant="filled" onClick={handleAddTableRow}>
                  {t("outage.button.add")}
                </Button>
                <Button
                  aria-label="outage-clear-generic"
                  color="gray"
                  variant="outline"
                  onClick={() => {
                    setRows([]);
                    setDailyMinutes({ start: null, end: null });
                    setWeeklyRange(null);
                    if (outageType === 'noRepetition') {
                      handleInputChange("componentAttributes", "outageStartDate", null);
                      handleInputChange("componentAttributes", "outageEndDate", null);
                    }
                    handleInputChange("componentAttributes", "outageDates", []);
                  }}
                >
                  {t("outage.button.clear")}
                </Button>
              </div>
            )}
          </Grid.Col>
        </Grid>
      )}
      {outageType !== "fullYear" && rows?.length ? (
        <Grid gutter="md" className="mt-2 mb-2">
          <Grid.Col span={10}>
            <MantineReactTable table={tableData} />
          </Grid.Col>
        </Grid>
      ) : null}
      <Text
        color={colorScheme === "dark" ? "#d5d7e0" : "black"}
        component="span"
        fw={700}
      >
        {t("outageManged")}
      </Text>
      <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col {...responsiveCol}>
          <Radio.Group
            value={outageOccurDurationType}
            {...form.getInputProps(
              "uiAttributes.gridParameters.outageOccurDuration.backup"
            )}
            onChange={(value) => handleOutageOccurDurationTypeChange(value)}
          >
            <Radio
              className="mt-2 mb-2"
              label={t("backup")}
              name="outage_occur_duration.full_backup"
              value="fullBackUp"
              {...getFieldAttributes("field-15")}
              data-id="field-15"
            />
            <Radio
              className="mt-2 mb-2"
              label={t("curtailedLoad")}
              id="curtailedLoadRadio"
              name="outage_occur_duration.full_backup"
              value="curtailedLoad"
              {...getFieldAttributes("field-16")}
              data-id="field-16"
            />
          </Radio.Group>
          <TextInput
            {...getFieldAttributes("field-17")}
            data-id="field-17"
            className="mb-2"
            id="curtailedLoad"
            type="number"
            autoComplete="none"
            name="outage_occur_duration.curtailed_load_value"
            placeholder={t("outage.loadValue.placeholder")}
            maxLength="100"
            required
            value={
              form?.values?.uiAttributes?.gridParameters?.outageOccurDuration
                ?.backup === "fullBackUp"
                ? 100
                : form?.values?.uiAttributes?.gridParameters
                    ?.outageOccurDuration?.loadValue
            }
            {...form.getInputProps(
              "uiAttributes.gridParameters.outageOccurDuration.loadValue"
            )}
            onChange={(e) => {
              const trimmedValue = formatInputValue(e.currentTarget.value);
              handleInputChange(
                "uiAttributes",
                "outageOccurDuration.loadValue",
                trimmedValue
              );
            }}
            disabled={
              form?.values?.uiAttributes?.gridParameters?.outageOccurDuration
                ?.backup === "fullBackUp"
            }
          />
        </Grid.Col>
      </Grid>
    </div>
  );
});

OutageLoadManagement.propTypes = {
  form: PropTypes.object,
  handleInputChange: PropTypes.func,
  gridParameters: PropTypes.object,
};

export default OutageLoadManagement;
